import { Box, useMediaQuery } from "@mui/material";
import { GridColumnVisibilityModel, GridPreferencePanelsValue } from "@mui/x-data-grid";
import { useGridApiRef } from "@mui/x-data-grid-pro";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { LicenseInfo } from "@mui/x-license-pro";
import React, { useEffect, useMemo, useState } from "react";
import { CachePolicies } from "use-http";

import { useImageQueriesFiltersByType } from "api/services/useImageQueriesFiltersByType";
import { ExpirePlanBar } from "components/common/ExpirePlanBar/ExpirePlanBar";
import { SubNavBar } from "components/common/SubNavBar";
import { TerminationNoticeBar } from "components/common/TerminationNoticeBar/TerminationNoticeBar";
import { FilterPosichecks } from "components/dataGridFilter/FilterPosichecks";
import { getConfig } from "config/config";
import { useAppDispatch, useAppSelector } from "hooks/store";
import { useColumnVisibility } from "hooks/useColumnVisibility";
import { useDataFilterState } from "hooks/useDataFilterState";
import { useFetchCustom } from "hooks/useFetchCustom";
import { SelectDetector } from "new-components/selectDetector/SelectDetector";
import { getImageQueries } from "store/services/ImageQueries/imageQueriesThunks";
import { getUserState } from "store/slices/Auth/authSlice";
import { handleResetImageQueries } from "store/slices/ImageQueries/imageQueriesSlice";
import { ListResponse } from "types/api";
import { DetectorModes } from "types/createDetector";
import { Detector } from "types/detectors";

import { ActionsMenu } from "./components/dataGrid/actionsMenu/ActionsMenu";
import { ImageQueriesContextProvider } from "./components/dataGrid/context";
import { DataGrid } from "./components/dataGrid/DataGrid";
import { DataPageOnboarding } from "./components/DataPageOnboarding";
import { GridView } from "./components/GridView";
import { VisibilityActions } from "./components/VisibilityActions";

const config = getConfig();
const { JANZU_URL } = config;
if (config.REACT_APP_MUI_LICENSE_KEY) {
  LicenseInfo.setLicenseKey(config.REACT_APP_MUI_LICENSE_KEY);
}

export function DataPageRoute() {
  return (
    <Box id="data-page-route">
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <DataPage />
      </LocalizationProvider>
      <DataPageOnboarding />
    </Box>
  );
}

export type CursorPagination = {
  cursor: string;
  direction: "next" | "previous";
};

export function DataPage() {
  // States
  const [gridSize, setGridSize] = useState(1);
  const [cursorPagination, setCursorPagination] = useState<CursorPagination>({
    cursor: "",
    direction: "next",
  });

  // get user data
  const userInfo = useAppSelector(getUserState);

  // Redux
  const {
    data: { results: imageQueries, count },
    pageSize,
  } = useAppSelector((state) => state.imageQueries);
  const dispatch = useAppDispatch();

  const IMAGE_QUERIES_BASE_URL = `${JANZU_URL}reef-api/image-queries?`;

  // API Ref
  const apiRef = useGridApiRef();

  // States and logic for filters
  const {
    parameterizedUrl: imageQueryUrl,
    filterState,
    filtersObject,
    onFilterChange,
    onFilterModelChange,
  } = useDataFilterState({
    serverFilterBaseUrl: IMAGE_QUERIES_BASE_URL,
    pageSize,
    cursorImageQueryId: cursorPagination,
  });

  // Getting detector data
  const url = `${JANZU_URL}reef-api/detectors/${filterState.detector_id}`;
  const {
    get: getDetector,
    data: detectorData,
    loading: isDetectorLoading,
    error: detectorError,
  } = useFetchCustom<Detector>(url, {
    cachePolicy: CachePolicies.NO_CACHE,
  });
  const detector = detectorError ? undefined : detectorData;

  // Column visibility
  const { columnVisibilityModel, setColumnVisibilityModel } = useColumnVisibility({ mode: detector?.mode });

  const isExportButtonHidden = useMemo(() => {
    if (filtersObject?.detector_id === "" || detector?.mode === undefined) return true;
    if (detector?.mode === DetectorModes.COUNT) return true;
    return false;
  }, [detector?.mode, filtersObject?.detector_id]);

  // Functions
  const handleChangeCursorPagination = (newCursorPagination: CursorPagination) =>
    setCursorPagination(newCursorPagination);

  const handleChangeColumnVisibilityModel = (newModel: GridColumnVisibilityModel) => {
    setColumnVisibilityModel(newModel);
  };

  const handleClickOpenColumnsPanel = () => {
    apiRef.current?.showPreferences(GridPreferencePanelsValue.columns);
    apiRef.current?.hidePreferences();
  };

  const { data: detectorsCount, loading: loadingDetectorsCount } = useFetchCustom<ListResponse<Detector>>(
    `${JANZU_URL}reef-api/detectors?page_size=1`,
    {
      cachePolicy: CachePolicies.NO_CACHE,
    },
    []
  );
  const hasDetectors = detectorsCount !== undefined && detectorsCount.count > 0;

  const hasDetectorSelected = Boolean(detector?.id);
  const shouldShowPulseAnimation = hasDetectors && !hasDetectorSelected && !isDetectorLoading;

  const { imageQueriesFiltersByType = { filters: [] } } = useImageQueriesFiltersByType({
    getOptions: {
      params: {
        detectorId: filterState.detector_id,
      },
    },
  });
  const hasFilters = imageQueriesFiltersByType.filters.length > 0;

  const isMobile = useMediaQuery("(max-width: 768px)");

  let height = "h-[calc(100vh-100px)]";
  if (userInfo.show_inspections) {
    height = "h-[calc(100vh-150px)]";
  } else if (isMobile) {
    height = "h-[calc(100vh-50px)]";
  }

  const filterableColumns = useMemo(
    () => imageQueriesFiltersByType.filters.map((filter) => filter.columnField) ?? [],
    [imageQueriesFiltersByType]
  );

  // useEffect to obtain detector data
  useEffect(() => {
    if (filterState.detector_id) {
      getDetector();
    }
  }, [filterState.detector_id, getDetector]);

  // useEffect to get image queries
  useEffect(() => {
    let promise: any;
    if (detector) {
      promise = dispatch(getImageQueries({ url: imageQueryUrl }));
    }
    return () => {
      if (promise?.abort) {
        promise.abort();
      }
    };
  }, [detector, imageQueryUrl, dispatch]);

  // Reset Redux on unmount
  useEffect(
    () => () => {
      dispatch(handleResetImageQueries());
    },
    [dispatch]
  );

  // Component render
  return (
    <Box id="data-page" className={height}>
      <SubNavBar>
        <SelectDetector
          actualRows={imageQueries.length}
          detectorId={filterState.detector_id}
          detectorName={detector?.name || ""}
          onFilterChange={onFilterChange}
          loadingDetectorData={isDetectorLoading}
          rowCount={count}
          showPulseAnimation={shouldShowPulseAnimation}
        />
      </SubNavBar>
      <ExpirePlanBar />
      <TerminationNoticeBar />
      <Box
        id="data-page-container-temp"
        className="h-[calc(100%-50px)]"
        sx={{
          px: { xs: "10px", sm: "20px", md: "30px" },
          pb: "20px",
        }}
      >
        <Box className="h-[50px] hidden sm:flex flex-col sm:flex-row gap-4 justify-end items-center">
          {!filterState.grid_view && (
            <ActionsMenu
              hideExportButton={isExportButtonHidden}
              handleClickOpenColumnsPanel={handleClickOpenColumnsPanel}
              columnVisibilityModel={columnVisibilityModel}
              filtersObject={filtersObject}
            />
          )}
          <VisibilityActions
            gridView={filterState.grid_view}
            setGridView={onFilterChange}
            gridSize={gridSize}
            setGridSize={setGridSize}
          />
        </Box>

        {hasFilters && (
          <FilterPosichecks
            detector={detector}
            filterState={filterState}
            isDisabled={!filterState.detector_id}
            onFilterChange={onFilterModelChange}
            filterableElements={imageQueriesFiltersByType.filters}
          />
        )}

        <Box className={hasFilters ? "h-[calc(100%-100px)]" : "h-[calc(100%-50px)]"}>
          {filterState.grid_view ? (
            <GridView gridSize={gridSize} filtersObject={filtersObject} detectorData={detector} />
          ) : (
            <ImageQueriesContextProvider detector={detector} hasDetectors={hasDetectors}>
              <DataGrid
                apiRef={apiRef}
                detector={detector}
                isDetectorLoading={isDetectorLoading || loadingDetectorsCount}
                imageQueryUrl={imageQueryUrl}
                columnVisibilityModel={columnVisibilityModel}
                onChangeColumnVisibilityModel={handleChangeColumnVisibilityModel}
                filterState={filterState}
                onFilterStateChange={onFilterChange}
                onChangeCursorPagination={handleChangeCursorPagination}
                filterableColumns={filterableColumns}
              />
            </ImageQueriesContextProvider>
          )}
        </Box>
      </Box>
    </Box>
  );
}
